import React, { useEffect, useState } from 'react';
import { Avatar, Box, Chip, Stack, Typography, alpha } from '@mui/material';
import {
  LocationOnRounded,
  Inventory2Rounded,
  RecyclingRounded,
  FactoryRounded,
  MapRounded,
  PublicRounded,
  ApartmentRounded,
  CategoryRounded,
  ScienceRounded,
} from '@mui/icons-material';
import { getFacilitiyByIdV2 } from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import { Empty } from 'antd';
import { properCase } from '../../../../helpers/utils';

const BRAND = '#0c5bab';


export default function ViewFacilityDetails({ facilityId }) {
  const [facilityItem, setFacilityItem] = useState();
  const [facilityFetching, setFacilityFetching] = useState(false);

  useEffect(() => {
    if (facilityId) {
      handleFetchFacilityItem();
    }
  }, [facilityId]);

  const handleFetchFacilityItem = async () => {
    setFacilityFetching(true);
    try {
      const res = await getFacilitiyByIdV2(facilityId);
      const { data, status } = res?.result;
      if (status?.code === '00') {
        setFacilityItem(data);
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      openNotification({ type: 'error', message: 'Unable to fetch facility details.' });
    } finally {
      setFacilityFetching(false);
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'SUPER':
        return '#d32f2f';
      case 'LARGE':
        return '#ed6c02';
      case 'MEDUIM':
        return '#1976d2';
      case 'SMALL':
        return '#2e7d32';
      case 'MINI':
        return '#9c27b0';
      default:
        return '#757575';
    }
  };

  const renderSectionHeader = (icon, title) => (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: '8px',
          display: 'grid',
          placeItems: 'center',
          bgcolor: alpha(BRAND, 0.08),
          color: BRAND,
          '& svg': { fontSize: 16 },
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle2" fontWeight={700}>
        {title}
      </Typography>
    </Stack>
  );

  const renderInfoTile = ({ icon, label, value }) => (
    <Box
      key={label}
      sx={{
        p: 1.25,
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: '#fff',
        minWidth: 0,
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'text.secondary', mb: 0.25 }}>
        {icon}
        <Typography variant="caption">{label}</Typography>
      </Stack>
      <Typography variant="body2" fontWeight={600} noWrap title={value || ''}>
        {value || 'N/A'}
      </Typography>
    </Box>
  );

  const renderAssetGroup = ({ title, icon, items, getKey, getLabel, chipSx }) => (
    <Box
      sx={{
        p: 1.5,
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: '#fff',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'text.secondary' }}>
          {icon}
          <Typography variant="body2" fontWeight={600}>
            {title}
          </Typography>
        </Stack>
        <Chip
          label={items?.length || 0}
          size="small"
          sx={{ height: 22, fontWeight: 600, bgcolor: alpha(BRAND, 0.08), color: BRAND }}
        />
      </Stack>
      {items?.length ? (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {items.map((i) => (
            <Chip
              key={getKey(i)}
              label={getLabel(i)}
              size="small"
              sx={{ borderRadius: '6px', fontWeight: 600, ...chipSx }}
            />
          ))}
        </Stack>
      ) : (
        <Typography variant="caption" color="text.disabled" fontStyle="italic">
          None recorded
        </Typography>
      )}
    </Box>
  );

  const renderMainDisplay = () => {
    if (facilityFetching) {
      return (
        <Box sx={{ p: 3 }}>
          <LoaderSkeleton />
          <LoaderSkeleton />
          <LoaderSkeleton />
        </Box>
      );
    }

    if (!facilityItem) {
      return (
        <Box sx={{ py: 10, textAlign: 'center' }}>
          <Empty description="No facility details found" />
        </Box>
      );
    }

    const catColor = getCategoryColor(facilityItem?.facilityCategoryName);
    const isActive = facilityItem?.status === 'ACTIVE';

    return (
      <Box>
        {/* HERO HEADER */}
        <Box
          sx={{
            p: 3,
            pb: 2.5,
            borderRadius: '16px',
            background: `linear-gradient(135deg, ${alpha(BRAND, 0.1)} 0%, ${alpha(BRAND, 0.02)} 100%)`,
            border: '1px solid',
            borderColor: alpha(BRAND, 0.12),
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700} lineHeight={1.25}>
                {facilityItem?.facilityName}
              </Typography>
              {facilityItem?.sectorName && (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5, color: 'text.secondary' }}>
                  <FactoryRounded sx={{ fontSize: 15 }} />
                  <Typography variant="body2">
                    {facilityItem.sectorName}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
            <Chip
              label={properCase(facilityItem?.status)}
              size="small"
              icon={
                <Box
                  component="span"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: isActive ? '#2e7d32' : '#ed6c02',
                    ml: '8px !important',
                  }}
                />
              }
              sx={{
                fontWeight: 600,
                bgcolor: '#fff',
                border: '1px solid',
                borderColor: 'divider',
                color: isActive ? '#2e7d32' : '#ed6c02',
              }}
            />
            {facilityItem?.facilityCategoryName && (
              <Chip
                label={`${properCase(facilityItem.facilityCategoryName)} category`}
                size="small"
                icon={<CategoryRounded sx={{ fontSize: '15px !important', color: `${catColor} !important` }} />}
                sx={{ fontWeight: 600, bgcolor: alpha(catColor, 0.1), color: catColor }}
              />
            )}
          </Stack>
        </Box>

        <Stack spacing={3} sx={{ px: 1, pt: 3, pb: 2 }}>
          {/* LOCATION */}
          <Box>
            {renderSectionHeader(<MapRounded />, 'Location')}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
              {[
                { icon: <PublicRounded sx={{ fontSize: 14 }} />, label: 'Zone', value: facilityItem?.zoneName },
                { icon: <MapRounded sx={{ fontSize: 14 }} />, label: 'State', value: facilityItem?.stateName },
                { icon: <ApartmentRounded sx={{ fontSize: 14 }} />, label: 'LGA', value: facilityItem?.lgaName },
              ].map(renderInfoTile)}
            </Box>
            {facilityItem?.address && (
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  mt: 1,
                  p: 1.25,
                  borderRadius: '12px',
                  bgcolor: alpha('#f4f6f8', 0.9),
                }}
              >
                <LocationOnRounded sx={{ fontSize: 18, color: BRAND, mt: 0.1 }} />
                <Typography variant="body2" color="text.secondary" lineHeight={1.5}>
                  {facilityItem.address}
                </Typography>
              </Stack>
            )}
          </Box>

          {/* OPERATIONAL ASSETS */}
          <Box>
            {renderSectionHeader(<Inventory2Rounded />, 'Operational Assets')}
            <Stack spacing={1}>
              {renderAssetGroup({
                title: 'Products',
                icon: <Inventory2Rounded sx={{ fontSize: 15 }} />,
                items: facilityItem?.facilityProduct,
                getKey: (i) => i.productId,
                getLabel: (i) => i.productName,
                chipSx: { bgcolor: alpha(BRAND, 0.06), color: '#0a4a8c' },
              })}
              {renderAssetGroup({
                title: 'Raw Materials',
                icon: <ScienceRounded sx={{ fontSize: 15 }} />,
                items: facilityItem?.facilityRawMaterial,
                getKey: (i) => i.rawMaterialId,
                getLabel: (i) => i.rawMaterialName,
                chipSx: { bgcolor: alpha('#ed6c02', 0.08), color: '#a14a00' },
              })}
              {renderAssetGroup({
                title: 'Waste Streams',
                icon: <RecyclingRounded sx={{ fontSize: 15 }} />,
                items: facilityItem?.facilityWaste,
                getKey: (i) => i.wasteId,
                getLabel: (i) => i.wasteName,
                chipSx: { bgcolor: alpha('#2e7d32', 0.08), color: '#1e5e22' },
              })}
            </Stack>
          </Box>
        </Stack>
      </Box>
    );
  };

  return <Box sx={{ height: '100%' }}>{renderMainDisplay()}</Box>;
}
